/**
 * RetroSignatureRepair — R126 · Feb 2026
 *
 * Rendered inside Settings → Email, below the two signature cards.
 *
 * Flow:
 *   1. "Scan" pings /workspaces/{ws}/mail/retro-signature/scan and
 *      shows each historical email whose stored HTML still carries
 *      base64 `data:image/…` `<img>` tags (i.e., broken for the
 *      original recipient).
 *   2. Per-row "Repair" rewrites the stored HTML so future in-app
 *      viewers render the signature correctly. "Repair + Resend" also
 *      re-delivers a corrected copy to the same recipient with a
 *      small banner explaining the re-render.
 *   3. Bulk actions apply to every scanned row.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import {
  Wrench, RefreshCw, Send, ShieldCheck, Loader2, Mail, Rocket,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); }
  catch { return iso.slice(0, 10); }
}

export default function RetroSignatureRepair() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [busyIds, setBusyIds] = useState({});
  const [bulk, setBulk] = useState(false);

  // R129 · Feb 2026 — Workspace-wide background repair job. Polled
  // every 2s while a job is running; drops back to null on completion.
  const [job, setJob] = useState(null);
  const [jobBusy, setJobBusy] = useState(false);
  const pollRef = useRef(null);

  const scan = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setScanning(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/mail/retro-signature/scan?limit=200`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Scan failed")); }
    finally { setScanning(false); }
  }, [activeWorkspaceId]);

  useEffect(() => { scan(); }, [scan]);

  // R129 · Poll job progress while active.
  useEffect(() => {
    if (!job || job.status === "done" || job.status === "failed") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(
          `/workspaces/${activeWorkspaceId}/mail/retro-signature/job/${job.id}`);
        setJob(data);
        if (data.status === "done" || data.status === "failed") {
          toast.success(`Bulk repair ${data.status} · repaired ${data.done}${data.resent ? ` · resent ${data.resent}` : ""}${data.failed ? ` · ${data.failed} failed` : ""}`);
          scan();
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [job?.id, job?.status, activeWorkspaceId]);

  const startBulk = async (resend) => {
    if (resend && !window.confirm(
      "Repair AND re-send corrected copies for every broken email in this workspace? " +
      "This will send fresh emails to every historical recipient.")) return;
    if (!resend && !window.confirm(
      "Repair every broken email in this workspace? " +
      "The in-app viewer will render correctly for future opens; nothing is sent to recipients.")) return;
    setJobBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/mail/retro-signature/repair-all`,
        { resend, batch_size: 25 });
      setJob({ id: data.job_id, status: "starting", total: 0, done: 0, resent: 0, failed: 0 });
      toast.success(data.resumed ? "Resumed running job" : "Bulk repair started");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk repair failed to start"));
    } finally { setJobBusy(false); }
  };

  const repair = async (activityIds, resend) => {
    if (!activityIds.length) return;
    if (resend && !window.confirm(
      `Re-send corrected copies to ${activityIds.length} recipient(s)? ` +
      "They'll get a fresh email with a small 're-render' banner.")) return;
    // Mark rows busy
    setBusyIds((prev) => {
      const next = { ...prev };
      activityIds.forEach((id) => { next[id] = true; });
      return next;
    });
    if (activityIds.length > 1) setBulk(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/mail/retro-signature/repair`,
        { activity_ids: activityIds, resend });
      toast.success(
        `Repaired ${data.repaired}${resend ? ` · resent ${data.resent}` : ""}` +
        (data.skipped ? ` · skipped ${data.skipped}` : "") +
        (data.errors?.length ? ` · ${data.errors.length} error(s)` : ""));
      // Drop repaired rows from the list.
      setRows((prev) => prev.filter((r) => !activityIds.includes(r.id)
                                             || data.errors?.some((e) => e.id === r.id)));
    } catch (e) {
      toast.error(apiErrorMessage(e, "Repair failed"));
    } finally {
      setBusyIds((prev) => {
        const next = { ...prev };
        activityIds.forEach((id) => { delete next[id]; });
        return next;
      });
      setBulk(false);
    }
  };

  return (
    <div className="border bg-white p-3" data-testid="retro-signature-repair">
      <div className="flex items-center gap-2 mb-1">
        <Wrench className="w-4 h-4 text-amber-600" />
        <div>
          <div className="text-sm font-semibold">Retro signature repair</div>
          <div className="text-[11px] text-muted-foreground">
            Older sent emails may still carry embedded (base64) signature images that
            Gmail/Outlook strip for the recipient. Repair rewrites the stored copy so
            future in-app views render correctly. Optionally re-send a corrected copy
            to the original recipient.
          </div>
        </div>
        <Button size="sm" variant="outline" className="rounded-none ml-auto"
                onClick={scan} disabled={scanning}
                data-testid="retro-sig-rescan">
          {scanning
            ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Scanning…</>
            : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Re-scan</>}
        </Button>
      </div>

      {/* R129 · Bulk workspace-wide progress bar */}
      {job && (
        <div className="mt-2 mb-1 border border-amber-300 bg-amber-50 px-3 py-2"
             data-testid="retro-sig-bulk-progress">
          <div className="flex items-center gap-2 text-xs">
            <Rocket className="w-3.5 h-3.5 text-amber-700" />
            <span className="font-semibold">
              Bulk repair · {job.status}
            </span>
            <span className="ml-auto font-mono tabular-nums text-amber-800"
                  data-testid="retro-sig-bulk-counts">
              {job.done || 0} / {job.total || 0} repaired
              {job.resent ? ` · ${job.resent} resent` : ""}
              {job.failed ? ` · ${job.failed} failed` : ""}
            </span>
          </div>
          <div className="mt-1 h-2 bg-amber-200 relative overflow-hidden">
            <div
              className={`h-2 ${job.status === "done" ? "bg-emerald-500" : "bg-amber-500"} transition-all`}
              style={{
                width: job.total > 0 ? `${Math.min(100, Math.round((job.done / job.total) * 100))}%` : "0%",
              }}
              data-testid="retro-sig-bulk-bar"
            />
          </div>
          {job.errors_head?.length > 0 && (
            <details className="mt-1 text-[10px] text-amber-900">
              <summary>{job.errors_head.length} error{job.errors_head.length === 1 ? "" : "s"}</summary>
              <ul className="ml-4 mt-1 list-disc">
                {job.errors_head.map((e, i) => (
                  <li key={i}><code>{e.id?.slice(0, 8)}…</code>: {e.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {scanning ? (
        <div className="text-xs text-muted-foreground text-center py-6">Scanning outbox…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-emerald-700 flex items-center gap-2 py-4 border-t border-border mt-2"
             data-testid="retro-sig-empty">
          <ShieldCheck className="w-4 h-4" />
          Nothing to repair — every stored outbound email uses inline (cid:) images already.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border">
            <Badge variant="outline" className="rounded-none" data-testid="retro-sig-total">
              {total} email{total === 1 ? "" : "s"} need repair
            </Badge>
            {rows.length < total && (
              <span className="text-[10px] text-muted-foreground">
                Showing the {rows.length} most recent.
              </span>
            )}
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="rounded-none"
                      onClick={() => startBulk(false)}
                      disabled={jobBusy || (job && job.status === "running")}
                      data-testid="retro-sig-bulk-repair">
                <Rocket className="w-3 h-3 mr-1" /> Repair ALL in workspace
              </Button>
              <Button size="sm" variant="outline" className="rounded-none"
                      onClick={() => repair(rows.map((r) => r.id), false)}
                      disabled={bulk} data-testid="retro-sig-repair-all">
                {bulk ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wrench className="w-3 h-3 mr-1" />}
                Repair visible (in-app only)
              </Button>
              <Button size="sm" className="rounded-none"
                      onClick={() => repair(rows.map((r) => r.id), true)}
                      disabled={bulk} data-testid="retro-sig-repair-resend-all">
                {bulk ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                Repair + re-send visible
              </Button>
            </div>
          </div>

          <div className="mt-2 border border-border max-h-80 overflow-y-auto">
            <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <div className="col-span-1">Date</div>
              <div className="col-span-3">Recipient</div>
              <div className="col-span-4">Subject</div>
              <div className="col-span-1 text-right">Imgs</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            {rows.map((r) => {
              const busy = !!busyIds[r.id];
              return (
                <div key={r.id}
                     className="grid grid-cols-12 gap-2 px-3 py-1.5 items-center border-t border-border text-xs hover:bg-muted/20"
                     data-testid={`retro-sig-row-${r.id}`}>
                  <div className="col-span-1 font-mono text-[10px]">{fmtDate(r.created_at)}</div>
                  <div className="col-span-3 truncate flex items-center gap-1" title={r.to}>
                    <Mail className="w-3 h-3 text-muted-foreground shrink-0" />
                    {Array.isArray(r.to) ? r.to.join(", ") : (r.to || "—")}
                  </div>
                  <div className="col-span-4 truncate" title={r.subject}>{r.subject || "(no subject)"}</div>
                  <div className="col-span-1 text-right font-mono tabular-nums text-amber-700">
                    {r.data_uri_count}
                  </div>
                  <div className="col-span-3 flex items-center justify-end gap-1">
                    <Button size="sm" variant="outline" className="rounded-none h-7 text-[11px]"
                            onClick={() => repair([r.id], false)} disabled={busy}
                            data-testid={`retro-sig-repair-${r.id}`}>
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Repair"}
                    </Button>
                    <Button size="sm" className="rounded-none h-7 text-[11px]"
                            onClick={() => repair([r.id], true)} disabled={busy}
                            data-testid={`retro-sig-resend-${r.id}`}>
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Repair + resend"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
